/**
 * THE ROUTE-CHROME SLOT — one place in the header for Back and the path (#2349 §A).
 *
 * The mock's observation is a space argument and it is right: *"the breadcrumbs
 * are now in the header. This saves more space and is very clean."* On this base
 * the record page drew its own `<nav aria-label="Breadcrumb">` as the first
 * element of the page body, so every record spent a row of the content column on
 * chrome — above a page header that then spent another one on the title.
 *
 * ── what this is NOT ────────────────────────────────────────────────────────
 * It is not a second trail model, and it parses nothing out of a URL. The
 * ancestors still come from `record/trail.ts` — the sessionStorage journey a
 * relationship navigation writes — and the labels are still the route's own,
 * resolved from the schema and the record it already read. What moves is WHERE
 * the result is drawn, and nothing about how it is derived. #2349 §A says so in
 * terms: *"routes remain the source of dynamic labels and unregister on unmount.
 * Reuse the existing breadcrumb/record-trail models; do not parse a competing
 * trail from URLs."*
 *
 * ── a module store, because the two ends are not in one React tree ──────────
 * `Topbar` is a sibling of the router outlet, not an ancestor, so a context
 * provider would have to wrap the whole shell to let a leaf route publish
 * upward. `packages/ui/src/shell/context.ts` already answers this exact shape
 * with a module-level store read through `useSyncExternalStore`, and this is
 * that pattern copied deliberately rather than a second mechanism.
 *
 * ── UNREGISTER ON UNMOUNT IS THE WHOLE CONTRACT ────────────────────────────
 * A declaration that outlives its route is a header describing a page nobody is
 * on — and it is worse than a missing one, because Back then navigates somewhere
 * the person did not come from. So `useRouteChrome` clears on unmount, and the
 * clear is guarded by identity: a route that mounts before the previous one
 * unmounts (which is ordinary React reconciliation) must not have ITS
 * declaration wiped by the departing route's cleanup.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react'

/** One ancestor in the path. The same `{label, to}` shape `record/trail.ts` stores. */
export interface RouteCrumb {
  label: string
  to: string
}

export interface RouteChrome {
  /**
   * Where Back goes, and what it is called.
   *
   * Absent renders NO Back control. A route with nowhere to return to must not
   * draw one: a button labelled "Back" that goes to a guess is the defect
   * `trail.ts` was written to end.
   */
  back?: RouteCrumb
  /** The ancestors that led here, oldest first. The current record is NOT one. */
  crumbs: RouteCrumb[]
}

let current: RouteChrome | null = null
/** Who declared it. A departing route may only clear its OWN declaration. */
let owner: symbol | null = null
const subscribers = new Set<() => void>()

const notify = () => { subscribers.forEach((fn) => fn()) }
const subscribe = (fn: () => void) => { subscribers.add(fn); return () => { subscribers.delete(fn) } }

/** Read the current declaration. Null means no route has declared one. */
export function readRouteChrome(): RouteChrome | null { return current }

/** For tests: forget everything, so one spec cannot leak into the next. */
export function resetRouteChrome(): void { current = null; owner = null; notify() }

/**
 * Declare this route's header chrome for as long as it is mounted.
 *
 * The value is compared STRUCTURALLY before it is published, because a route
 * rebuilds its crumb array on every render and publishing an equal-but-new array
 * would notify every subscriber on every keystroke in the form below it.
 */
export function useDeclareRouteChrome(chrome: RouteChrome | null): void {
  const identity = useRef<symbol | null>(null)
  if (identity.current === null) identity.current = Symbol('route-chrome')
  const serialized = JSON.stringify(chrome)

  useEffect(() => {
    const next = serialized === 'null' ? null : JSON.parse(serialized) as RouteChrome
    current = next
    owner = identity.current
    notify()
    return () => {
      // Only if nobody has declared since. A route that mounts before this one
      // unmounts owns the slot, and clearing it here would blank the header of
      // the page the person is actually on.
      if (owner !== identity.current) return
      current = null
      owner = null
      notify()
    }
  }, [serialized])
}

/** What the header should draw right now. */
export function useRouteChrome(): RouteChrome | null {
  return useSyncExternalStore(subscribe, readRouteChrome, () => null)
}
